import { ArrowRight } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Form } from 'react-router';

import NumberInput from 'DesignSystem/features/input/NumberInput';
import { Button } from 'DesignSystem/ui/button';
import { cn } from 'DesignSystem/utils';
import { prefCardsBootstrapQuery } from 'pages/preference-cards/api';
import { getActionTypeIdFromItem, selectActionNames } from 'pages/preference-cards/api/selectors';
import { getTooltipMessage } from 'pages/preference-cards/components/card-detail/actions/utils';
import type { PostPrefCardAction, PrefCardsItem } from 'pages/preference-cards/types';

import {
  useItemChangeAction,
  usePrefCardActionChangedItem,
  useRemoveItemChangeAction,
} from '../../action-context/reducer';

type Props = {
  item: PrefCardsItem;
  variant?: 'default' | 'pack';
};

export default function SideBarAction({ item, variant = 'default' }: Props) {
  const update = useItemChangeAction();
  const remove = useRemoveItemChangeAction();
  const changedItem = usePrefCardActionChangedItem(item.preference_card_client_item_master_id);
  const hasActiveSwap = changedItem?.swapParentId === item.preference_card_client_item_master_id;
  const { data: actions, isSuccess } = useQuery(prefCardsBootstrapQuery(selectActionNames));

  const hasAppliedChanges = Boolean(changedItem);
  const hasRecommendation = item.recommended_action_display_name !== null;
  const isPack = variant === 'pack';
  const [isCancelable, setIsCancelable] = React.useState(hasAppliedChanges && !hasActiveSwap);
  const showActions = !isPack || isCancelable || hasAppliedChanges || hasRecommendation;
  const openDefaultValue =
    !hasRecommendation && !changedItem?.change
      ? item.unit_open!
      : (changedItem?.change?.unit_open ?? item.unit_open_recommendation ?? item.unit_open ?? undefined);
  const holdDefaultValue =
    !hasRecommendation && !changedItem?.change
      ? item.unit_hold!
      : (changedItem?.change?.unit_hold ?? item.unit_hold_recommendation ?? item.unit_hold ?? undefined);
  const [currentChangedOpenCount, setCurrentChangedOpenCount] = React.useState<number | undefined>(openDefaultValue);
  const [currentChangedHoldCount, setCurrentChangedHoldCount] = React.useState<number | undefined>(holdDefaultValue);
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const key = changedItem ? `${changedItem.change.unit_open}|${changedItem.change.unit_hold}` : '0';

  if (!isSuccess) return null;

  const isSnoozed = item.snooze_until !== null;
  const isInPendingRequest = item.in_active_change_request === true;
  const isInputDisabled = hasAppliedChanges || isSnoozed || isInPendingRequest;

  const tooltipMessage = getTooltipMessage(isSnoozed, isInPendingRequest);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const formData = new FormData(event.currentTarget);
    const holdVal = formData.get('unit_hold');
    const openVal = formData.get('unit_open');
    if (
      typeof openVal === 'string' &&
      typeof holdVal === 'string' &&
      item.preference_card_client_item_master_id !== null &&
      typeof actions !== 'undefined'
    ) {
      const unit_hold = Number(holdVal);
      const unit_open = Number(openVal);

      // Check if the new values match the original values (no net change)
      if (unit_open === (item.unit_open ?? 0) && unit_hold === (item.unit_hold ?? 0)) {
        setValidationError('No changes detected. The quantities match the original values.');
        return;
      }

      const change: PostPrefCardAction = {
        preference_card_client_item_master_id: item.preference_card_client_item_master_id,
        action_type_id: getActionTypeIdFromItem(item, unit_open, unit_hold, actions),
        unit_hold,
        unit_open,
      };
      update({ change, item });
    }
    setIsCancelable(true);
  }

  function handleChange(updatedCount: number, name: string) {
    if (name === 'unit_open') {
      setCurrentChangedOpenCount(updatedCount);
    }
    if (name === 'unit_hold') {
      setCurrentChangedHoldCount(updatedCount);
    }

    let isOpenValueDifferent = updatedCount !== item.unit_open && name === 'unit_open';
    let isHoldValueDifferent = updatedCount !== item.unit_hold && name === 'unit_hold';
    if (hasRecommendation) {
      isOpenValueDifferent = updatedCount !== item.unit_open_recommendation && name === 'unit_open';
      isHoldValueDifferent = updatedCount !== item.unit_hold_recommendation && name === 'unit_hold';
    }
    setIsCancelable(isOpenValueDifferent || isHoldValueDifferent);
  }

  function handleReset() {
    if (changedItem && changedItem.item.preference_card_client_item_master_id) {
      remove(changedItem.item.preference_card_client_item_master_id);
    }
    if (hasRecommendation) {
      setCurrentChangedOpenCount(item.unit_open_recommendation!);
      setCurrentChangedHoldCount(item.unit_hold_recommendation!);
    } else {
      setCurrentChangedOpenCount(item.unit_open!);
      setCurrentChangedHoldCount(item.unit_hold!);
    }
    setValidationError(null);
    setIsCancelable(false);
  }

  return (
    <div>
      <Form action={`/preference-cards`} ref={formRef} onSubmit={handleSubmit} key={key}>
        <div className={cn('flex w-full items-center self-stretch rounded-md bg-zinc-100', !isPack && 'mb-3')}>
          <div className="flex w-full flex-col items-center justify-center">
            <div className="flex h-9 select-none items-center justify-center self-stretch rounded-tl-md bg-indigo-50 px-3 font-medium">
              Open Qty
            </div>
            <div className="flex items-center justify-center self-stretch px-3 py-2">
              <NumberInput
                name="unit_open"
                defaultValue={currentChangedOpenCount}
                disabled={isInputDisabled}
                insideLabel={
                  <CurrentUnitCountLabel
                    currentCount={item.unit_open!}
                    showCountLabel={hasAppliedChanges && currentChangedOpenCount !== item.unit_open}
                    hasRecommendation={
                      hasRecommendation &&
                      item.unit_open_recommendation !== null &&
                      item.unit_open !== item.unit_open_recommendation
                    }
                  />
                }
                onChange={handleChange}
                classNames={isPack ? 'h-12 w-full rounded-[5px] py-0' : 'w-32'}
                hideSteppers={isPack}
              />
            </div>
          </div>
          <div className="flex w-full flex-col items-center justify-center">
            <div className="flex h-9 select-none items-center justify-center self-stretch rounded-tr-md bg-indigo-50 px-3 font-medium">
              {isPack ? 'Hold Qty' : 'PRN Qty'}
            </div>
            <div className="flex items-center justify-center self-stretch px-3 py-2">
              <NumberInput
                name="unit_hold"
                defaultValue={currentChangedHoldCount}
                disabled={isInputDisabled}
                insideLabel={
                  <CurrentUnitCountLabel
                    currentCount={item.unit_hold!}
                    showCountLabel={hasAppliedChanges && currentChangedHoldCount !== item.unit_hold}
                    hasRecommendation={
                      hasRecommendation &&
                      item.unit_hold_recommendation !== null &&
                      item.unit_hold !== item.unit_hold_recommendation
                    }
                  />
                }
                onChange={handleChange}
                classNames={isPack ? 'h-12 w-full rounded-[5px] py-0' : 'w-32'}
                hideSteppers={isPack}
              />
            </div>
          </div>
        </div>

        {showActions ? (
          <div className={cn('flex flex-col gap-1', isPack && 'mt-3')}>
            <div className="flex flex-row space-x-2">
              <Button
                type="reset"
                disabled={Boolean(tooltipMessage) || !isCancelable}
                variant="destructive-ghost"
                onClick={handleReset}
                className="w-1/2 select-none"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={Boolean(tooltipMessage) || hasAppliedChanges || (!hasRecommendation && !isCancelable)}
                className="w-1/2 select-none"
              >
                {hasAppliedChanges ? 'Applied' : 'Save Changes'}
              </Button>
            </div>

            {validationError ? (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-sm text-amber-800">{validationError}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Form>
    </div>
  );
}

type CurrentUnitCountLabelProps = {
  currentCount: number;
  showCountLabel?: boolean;
  hasRecommendation?: boolean;
};
function CurrentUnitCountLabel({ currentCount, showCountLabel, hasRecommendation }: CurrentUnitCountLabelProps) {
  if (!hasRecommendation && !showCountLabel) return null;
  return (
    <div className="flex select-none items-center justify-center gap-1 font-medium">
      {currentCount}
      <ArrowRight className="h-3.5 w-3.5 text-zinc-600" />
    </div>
  );
}
